// Проверка доступа к уведомлениям, камере и микрофону (план K9b). Чистая логика определения
// состояния вынесена сюда, чтобы покрываться unit-тестами без DOM; UI — AccessNeededBanner.

export type AccessKind = 'notifications' | 'camera' | 'microphone';
export type PermState = 'granted' | 'prompt' | 'denied' | 'unknown';
export type AccessReason = 'prompt' | 'denied' | 'notFound' | 'notReadable';
export type BrowserKind = 'chrome' | 'edge' | 'firefox' | 'safari' | 'other';

export interface AccessProblem {
  kind: AccessKind;
  reason: AccessReason;
}

export interface AccessSnapshot {
  notifications: PermState;
  camera: PermState;
  microphone: PermState;
  /** Число устройств ввода; null — перечислить не удалось. */
  audioInputs: number | null;
  videoInputs: number | null;
}

/** Ошибка getUserMedia, пойманная при звонке. */
export interface MediaFailure {
  kind: 'camera' | 'microphone';
  name: string;
}

export const ACCESS_SHOWN_KEY = 'hc_access_banner_shown';
export const ACCESS_PERIOD_MS = 24 * 60 * 60 * 1000;

export function detectBrowser(ua: string): BrowserKind {
  if (/Edg(e|A|iOS)?\//.test(ua)) return 'edge';
  if (/Firefox\/|FxiOS\//.test(ua)) return 'firefox';
  if (/Chrome\/|CriOS\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}

/** Адрес страницы настроек сайта; null, если у браузера нет такого адреса. Открыть его из страницы нельзя — только скопировать. */
export function siteSettingsUrl(browser: BrowserKind, origin: string): string | null {
  if (browser === 'chrome') return `chrome://settings/content/siteDetails?site=${encodeURIComponent(origin)}`;
  if (browser === 'edge') return `edge://settings/content/siteDetails?site=${encodeURIComponent(origin)}`;
  return null;
}

export function mediaFailureReason(name: string): AccessReason | null {
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'notFound';
  if (name === 'NotReadableError' || name === 'AbortError') return 'notReadable';
  return null;
}

export function normalizePermState(value: string | undefined | null): PermState {
  return value === 'granted' || value === 'prompt' || value === 'denied' ? value : 'unknown';
}

/**
 * Список проблем по снимку состояния.
 * - `inCall`: браузер сам спросит доступ при начале звонка, поэтому `prompt` не проблема;
 *   показываем только запрет, отсутствие устройства и пойманную ошибку getUserMedia.
 *   Уведомления в звонке не проверяются.
 * - вне звонка: `prompt` — повод предложить кнопку «Разрешить»; отсутствие камеры не показываем
 *   (на ПК без камеры это норма), отсутствие микрофона — показываем.
 */
export function computeProblems(snap: AccessSnapshot, opts: { inCall: boolean; failure?: MediaFailure | null }): AccessProblem[] {
  const out: AccessProblem[] = [];
  const failure = opts.failure ?? null;

  if (!opts.inCall) {
    if (snap.notifications === 'denied') out.push({ kind: 'notifications', reason: 'denied' });
    else if (snap.notifications === 'prompt') out.push({ kind: 'notifications', reason: 'prompt' });
  }

  for (const kind of ['microphone', 'camera'] as const) {
    const state = snap[kind];
    const devices = kind === 'microphone' ? snap.audioInputs : snap.videoInputs;
    const failureReason = failure && failure.kind === kind ? mediaFailureReason(failure.name) : null;
    if (failureReason) {
      out.push({ kind, reason: failureReason });
    } else if (state === 'denied') {
      out.push({ kind, reason: 'denied' });
    } else if (devices === 0 && (kind === 'microphone' || failure?.kind === 'camera')) {
      out.push({ kind, reason: 'notFound' });
    } else if (state === 'prompt' && !opts.inCall) {
      out.push({ kind, reason: 'prompt' });
    }
  }
  return out;
}

/** Показывать ли баннер при запуске: не чаще раза в сутки. */
export function shouldShowAtStartup(lastShown: number | null, now: number): boolean {
  if (lastShown === null || !Number.isFinite(lastShown)) return true;
  return now - lastShown >= ACCESS_PERIOD_MS || now < lastShown;
}

export function readLastShown(): number | null {
  try {
    const raw = localStorage.getItem(ACCESS_SHOWN_KEY);
    return raw === null ? null : Number(raw);
  } catch {
    return null;
  }
}

export function writeLastShown(now: number): void {
  try {
    localStorage.setItem(ACCESS_SHOWN_KEY, String(now));
  } catch {
    /* localStorage недоступен — баннер просто покажется снова */
  }
}

async function queryOne(name: AccessKind): Promise<{ state: PermState; status: PermissionStatus | null }> {
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return { state: normalizePermState(status.state), status };
  } catch {
    // Firefox/Safari не знают часть имён; для уведомлений есть Notification.permission.
    if (name === 'notifications' && typeof Notification !== 'undefined') {
      const p = Notification.permission;
      return { state: p === 'default' ? 'prompt' : p, status: null };
    }
    return { state: 'unknown', status: null };
  }
}

async function countDevices(): Promise<{ audio: number | null; video: number | null }> {
  try {
    if (!navigator.mediaDevices?.enumerateDevices) return { audio: null, video: null };
    const list = await navigator.mediaDevices.enumerateDevices();
    return {
      audio: list.filter((d) => d.kind === 'audioinput').length,
      video: list.filter((d) => d.kind === 'videoinput').length,
    };
  } catch {
    return { audio: null, video: null };
  }
}

/** Опрашивает браузер. Возвращает снимок и PermissionStatus для подписки на onchange. */
export async function probeAccess(): Promise<{ snapshot: AccessSnapshot; statuses: PermissionStatus[] }> {
  const [n, c, m, d] = await Promise.all([queryOne('notifications'), queryOne('camera'), queryOne('microphone'), countDevices()]);
  return {
    snapshot: { notifications: n.state, camera: c.state, microphone: m.state, audioInputs: d.audio, videoInputs: d.video },
    statuses: [n.status, c.status, m.status].filter((s): s is PermissionStatus => s !== null),
  };
}
